import { createDebug } from "src/utils/LazyDebug";

import type { MoneyUtil } from "../utils/MoneyUtil";
import type { UrlUtil } from "../utils/UrlUtil";
import type {
  TrackingEvent,
  TrackingEventName,
  TrackingService,
} from "../api/TrackingApi";

const debug = createDebug("wk:tracking:klaviyo");

export class KlaviyoTracking implements TrackingService {
  eventNames: Map<TrackingEventName, string>;
  urlUtil: UrlUtil;
  moneyUtil: MoneyUtil;
  customerEmail: string;

  constructor({
    customerEmail,
    urlUtil,
    moneyUtil,
  }: {
    customerEmail: string;
    urlUtil: UrlUtil;
    moneyUtil: MoneyUtil;
  }) {
    this.customerEmail = customerEmail;
    this.urlUtil = urlUtil;
    this.moneyUtil = moneyUtil;
    this.eventNames = new Map([
      ["addToWishlist", "Added To Wishlist"],
      ["removeFromWishlist", "Removed From Wishlist"],
      ["clearWishlist", "Cleared Wishlist"],
    ]);
  }

  async track({
    eventName,
    wishlist,
    product,
    variant,
    quantity = 1,
  }: TrackingEvent) {
    const eventAction = this.eventNames.get(eventName);

    if (!eventAction || !wishlist || !product) {
      return false;
    }

    const productIds = wishlist.items
      .slice()
      .reverse()
      .map((item) => item.product.id);

    const identiti = {
      $email: this.customerEmail,
      WishlistUrl: this.urlUtil.getPublicWishlistUrl(wishlist.id),
      WishlistProductIDs: productIds,
    };

    debug("Identify", identiti);

    window.klaviyo?.push(["identify", identiti]);

    const price =
      variant?.price ?? !product.price_varies ? product.price_min : undefined;

    const event = {
      URL: this.urlUtil.getVariantUrl(product.url, variant?.id),
      Name: product.title,
      ProductID: product.id,
      VariantID: variant?.id ?? null,
      SKU: variant?.sku ?? null,
      Tags: product.tags,
      Brand: product.vendor,
      ImageURL: this.urlUtil.getImageUrl(
        variant?.featured_image?.src ? variant : product,
        { width: 1000 },
      ),
      Price: price ? price / 100 : null,
      PriceFormatted: price ? this.moneyUtil.formatMoney(price) : null,
      CurrencyCode: window.Shopify?.currency?.active ?? null,
      Quantity: quantity,
      WishlistUrl: this.urlUtil.getPublicWishlistUrl(wishlist.id),
      WishlistProductIDs: productIds,
    };

    debug("Track", eventAction, event);

    window.klaviyo?.push(["track", eventAction, event]);

    return true;
  }
}
